import { fetchJSON as fetchAPI } from './client';

export type SupplyPlan = {
  id: string;
  name: string;
  status: string;
  total_demand: number;
  total_supply: number;
  supply_gap: number;
  fulfillment_rate: number;
  service_level: number;
  supply_cost: number;
  health_score: number;
  resilience_index: number;
  supplier_dependency_index: number;
  capacity_utilization_score: number;
  constraint_count: number;
};

export type CapacityModel = {
  id: string;
  node_type: string;
  node_id: string;
  node_name: string;
  total_capacity: number;
  allocated_capacity: number;
  available_capacity: number;
  utilization_pct: number;
  constraint_active: boolean;
};

export type SupplySource = {
  id: string;
  source_type: string;
  source_id: string;
  source_name: string;
  available_qty: number;
  allocated_qty: number;
  lead_time_days: number;
  unit_cost: number;
  priority: number;
  reliability: number;
};

export type SupplyAllocation = {
  id: string;
  sku?: string;
  demand_qty: number;
  allocated_qty: number;
  unmet_qty: number;
  fulfillment_pct: number;
  primary_source_name: string;
  allocation_reason: string;
  confidence: number;
  expected_cost: number;
  service_impact: string;
};

export type SupplyConstraint = {
  id: string;
  type: string;
  node_name?: string;
  severity: string;
  description: string;
  impact: string;
  mitigation: string;
};

export type SupplyRisk = {
  id: string;
  risk_type: string;
  severity: string;
  probability: number;
  business_impact: string;
  mitigation_plan: string;
};

export type SupplyRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  business_explanation: string;
  confidence: number;
  expected_cost: number;
  expected_savings: number;
  service_impact?: string;
  expected_risk?: string;
  auto_execute: boolean;
  status: string;
};

export type SupplyScenario = {
  id: string;
  name: string;
  scenario_type: string;
  service_level: number;
  supply_cost: number;
  revenue: number;
  risk_score: number;
  inventory_impact: number;
  cash_flow_impact: number;
  description?: string;
};

export type SupplyDashboard = {
  active_plans: number;
  avg_fulfillment_rate: number;
  avg_service_level: number;
  supply_reliability: number;
  capacity_utilization: number;
  constraint_count: number;
  open_risks: number;
  pending_recommendations: number;
  health_score: number;
  resilience_index: number;
  supply_cost: number;
  allocation_efficiency: number;
};

const API_BASE = import.meta.env.VITE_SUPPLYPLANNING_API ?? '/api/v1';

function supplyFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const supplyPlanningApi = {
  dashboard: () => supplyFetch<SupplyDashboard>('/supply-dashboard'),
  listPlans: () => supplyFetch<SupplyPlan[]>('/supply-plans'),
  listCapacities: (planId?: string) => supplyFetch<CapacityModel[]>(`/supply-capacity${planId ? `?plan_id=${planId}` : ''}`),
  listSources: (planId?: string) => supplyFetch<SupplySource[]>(`/supply-sources${planId ? `?plan_id=${planId}` : ''}`),
  listAllocations: (planId?: string) => supplyFetch<SupplyAllocation[]>(`/supply-allocations${planId ? `?plan_id=${planId}` : ''}`),
  listConstraints: (planId?: string) => supplyFetch<SupplyConstraint[]>(`/supply-constraints${planId ? `?plan_id=${planId}` : ''}`),
  listRisks: (planId?: string) => supplyFetch<SupplyRisk[]>(`/supply-risks${planId ? `?plan_id=${planId}` : ''}`),
  listRecommendations: (planId?: string) => supplyFetch<SupplyRecommendation[]>(`/supply-recommendations${planId ? `?plan_id=${planId}` : ''}`),
  listScenarios: (planId?: string) => supplyFetch<SupplyScenario[]>(`/supply-scenarios${planId ? `?plan_id=${planId}` : ''}`),
  calculateAllocation: (planId: string) =>
    supplyFetch<SupplyAllocation>('/supply-allocation/calculate', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    }),
  approvePlan: (id: string) => supplyFetch<SupplyPlan>(`/supply-plans/${id}/approve`, { method: 'POST' }),
};
